import { Avatar, AvatarFallback } from '@neo/primitives/avatar'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Separator } from '@neo/primitives/separator'
import { Tooltip } from '@neo/primitives/tooltip'
import { getInitials } from '@shared/utils'
import {
  Building2,
  CreditCard,
  Globe,
  Loader2,
  Lock,
  LogOut,
  Settings2,
  Shield,
  ShieldCheck,
  UserSquare2,
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'router-v6'
import { useVoiceFeatureLock } from 'v2/api-console/shared/hooks/use-voice-access-gate'
import { REGIONS } from 'v2/configs/constants'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { canViewTrustAndLegal } from 'v2/pages/trust-and-legal/tabs'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useSettingsAccess } from 'v2/shared/hooks/use-settings-access'
import {
  type EnterpriseRequestSource,
  LOCKED_TOOLTIP,
  openBuddyEnterpriseRequest,
} from 'v2/shared/utils/buddy-enterprise-request'
import { getLocalAuth, getSessionRoles } from 'v2/utils'
import {
  getVisibleSettingsItems,
  resolveItemGateState,
  SETTINGS_PATHS,
  type SettingsMenuItem,
} from './user-avatar-popover-sections'

// Icons for the shallow items returned by getVisibleSettingsItems, keyed by
// the same SETTINGS_PATHS constants the helper builds its items from — so a
// rename in one place can't silently desync the icon lookup from the actual
// item path. Kept in the render layer so the helper itself stays pure data
// (no JSX) and unit-testable without rendering.
const SETTINGS_ITEM_ICONS: Record<string, React.ReactNode> = {
  [SETTINGS_PATHS.profileSettings]: <UserSquare2 size={16} />,
  [SETTINGS_PATHS.organizationSettings]: <Building2 size={16} />,
  [SETTINGS_PATHS.billing]: <CreditCard size={16} />,
  [SETTINGS_PATHS.plansAndPricing]: <CreditCard size={16} />,
  [SETTINGS_PATHS.endpoints]: <Settings2 size={16} />,
  [SETTINGS_PATHS.geoPermissions]: <Globe size={16} />,
  [SETTINGS_PATHS.trustAndLegal]: <ShieldCheck size={16} />,
}

const toTestId = (title: string) => title.toLowerCase().replace(/\s+/g, '-')

const MenuButton = ({
  title,
  icon,
  onClick,
  testId,
  locked,
  pending,
}: {
  title: string
  icon: React.ReactNode
  onClick: () => void
  testId: string
  // Enterprise-locked item: ghosted label + trailing lock icon and a hover
  // tooltip; the caller's onClick opens the upgrade flow instead of navigating.
  locked?: boolean
  // Entitlement still resolving: ghosted and inert, but no lock — showing one
  // would be the wrong guess for an account that turns out to be entitled.
  pending?: boolean
}) => {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-hv1 text-left text-s-400 disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={testId}
    >
      {locked ? (
        // Same layout as the locked sidebar items (WhatsApp / Verify): ghosted
        // row, title takes the free space so the lock sits flush right.
        <span className="flex items-center gap-3 w-full opacity-50">
          {icon}
          <span className="flex-1">{title}</span>
          <Lock className="h-3 w-3 shrink-0" strokeWidth={2.5} />
        </span>
      ) : (
        <>
          {icon}
          {title}
        </>
      )}
    </button>
  )

  return locked ? (
    <Tooltip content={LOCKED_TOOLTIP} delayDuration={300}>
      {button}
    </Tooltip>
  ) : (
    button
  )
}

export const UserAvatarPopover = () => {
  const [open, setOpen] = useState(false)
  const [logoutHappening, setLogoutHappening] = useState(false)
  const navigate = useNavigate()
  const { data: profileData } = useProfileQuery()

  // Geo Permissions apply only to the US data region, so hide the menu entry for
  // India orgs (matches how Compliance is hidden from the India navigation).
  const isIndianRegion = getLocalAuth().region === REGIONS.IN

  // Endpoints needs the `voice_enabled` grant OR an unlocked plan; without either the
  // item is ghosted and click opens the upgrade flow. Same hook as the page.
  const { isVoiceFeatureLocked, isVoiceLockLoaded } = useVoiceFeatureLock()
  const { isAdmin, canAccessUsers, canAccessBilling, canAccessEndpoints, canAccessGeoPermissions } =
    useSettingsAccess()
  const featureFlags = useFeatureFlags()
  const items = getVisibleSettingsItems({
    isAdmin,
    canAccessUsers,
    canAccessBilling,
    canAccessEndpoints,
    canAccessGeoPermissions,
    ssoSelfServeEnabled: featureFlags.ssoSelfServeEnabled,
    canViewTrustAndLegal: canViewTrustAndLegal({ roles: getSessionRoles(), featureFlags }),
  })
  const visibleItems: SettingsMenuItem[] = [
    items.profileSettings,
    items.organizationSettings,
    items.trustAndLegal,
    items.billing,
    items.geoPermissions,
    // Endpoints sits at the bottom, just above the Logout separator.
    items.endpoints,
  ].filter(
    (item) => item.visible && !(isIndianRegion && item.path === SETTINGS_PATHS.geoPermissions),
  )

  const userName = profileData
    ? `${profileData.account_first_name} ${profileData.account_last_name}`
    : undefined
  const email = profileData?.email
  const initials = getInitials(userName ?? email)

  const handleNavigate = (path: string) => {
    navigate(path)
    setOpen(false)
  }

  // Close the popover first so the Buddy panel isn't hidden behind it.
  const handleLockedClick = (source: EnterpriseRequestSource) => {
    setOpen(false)
    openBuddyEnterpriseRequest({ source })
  }

  const handleLogout = () => {
    if (!logoutHappening) {
      setLogoutHappening(true)
      window.cx.logout()
    }
  }

  const isSupportUser = !!profileData?.organizations?.[0]?.is_internal_support_user

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Open profile menu"
          className="relative inline-flex size-[30px] shrink-0 cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:opacity-90"
          data-testid="user-avatar-trigger"
        >
          <Avatar className="size-[30px] rounded-full">
            <AvatarFallback className="rounded-full bg-brand font-inter text-p-500 text-brand-foreground">
              {profileData ? initials : <Loader2 className="size-3 animate-spin" />}
            </AvatarFallback>
          </Avatar>
          {isSupportUser && (
            <span
              role="img"
              aria-label="Plivo support session"
              className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-status-warning ring-2 ring-background"
              data-testid="user-avatar-support-badge"
            >
              <Shield className="h-2 w-2 text-white" strokeWidth={3} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end" sideOffset={8}>
        {/* User Info Section */}
        <div className="flex items-center gap-3 p-3 border-b">
          <Avatar className="size-10 rounded-full">
            <AvatarFallback className="rounded-full bg-brand/10 dark:bg-brand/20 font-inter text-p-500 text-brand-text">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="text-p-600 truncate">{userName}</span>
            <span className="text-xs-400 text-muted-foreground truncate">{email}</span>
          </div>
        </div>

        {/* Menu Items — shallow, each opens a page with its own top tabs */}
        <div className="flex flex-col p-2">
          {visibleItems.map((item) => {
            const { pending, lockedSource } = resolveItemGateState(item.path, {
              isLocked: isVoiceFeatureLocked,
              isLockLoaded: isVoiceLockLoaded,
            })
            return (
              <MenuButton
                key={item.path}
                title={item.title}
                icon={SETTINGS_ITEM_ICONS[item.path]}
                onClick={
                  lockedSource
                    ? () => handleLockedClick(lockedSource)
                    : () => handleNavigate(item.path)
                }
                testId={`user-menu-${toTestId(item.title)}`}
                locked={!!lockedSource}
                pending={pending}
              />
            )
          })}
        </div>

        {/* Logout */}
        <Separator />
        <div className="p-2">
          <button
            type="button"
            onClick={handleLogout}
            disabled={logoutHappening}
            className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-hv1 text-left text-s-400 w-full text-destructive hover:text-destructive disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="user-menu-logout"
          >
            {logoutHappening ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            ) : (
              <LogOut size={16} strokeWidth={1.5} />
            )}
            {logoutHappening ? 'Logging out...' : 'Logout'}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
